import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface EventFilters {
  type: string;
  format: string;
  city: string;
  search: string;
}

interface EventData {
  id: string;
  title: string;
  slug: string;
  start_datetime: string;
  end_datetime: string;
  location_city: string | null;
  location_name: string | null;
  event_type: string | null;
  event_format: string | null;
  cover_image_url: string | null;
  price: number | null;
  currency: string | null;
  max_participants: number | null;
  current_participants: number | null;
  description_it: string | null;
  description_en: string | null;
  description_es: string | null;
}

async function fetchEvents(filters: EventFilters): Promise<EventData[]> {
  let query = supabase
    .from('events')
    .select('*')
    .eq('status', 'published')
    .gte('start_datetime', new Date().toISOString())
    .order('start_datetime', { ascending: true });

  if (filters.type !== 'all') {
    query = query.eq('event_type', filters.type);
  }
  if (filters.format !== 'all') {
    query = query.eq('event_format', filters.format);
  }
  if (filters.city !== 'all') {
    query = query.eq('location_city', filters.city);
  }
  if (filters.search) {
    query = query.or(`title.ilike.%${filters.search}%,description_it.ilike.%${filters.search}%`);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

export function useEventsSearch(filters: EventFilters) {
  return useQuery({
    queryKey: ['events', filters.type, filters.format, filters.city, filters.search],
    queryFn: () => fetchEvents(filters),
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes in cache
    placeholderData: (previousData) => previousData,
  });
}
